import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiCheckCircle, mdiCalendar, mdiClock, mdiCurrencyUsd, mdiCreditCardCheck, mdiInformationOutline, mdiCheck } from '@mdi/js';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrencyFromMinor } from '../utils/currency';
import { useMobileMenu } from '../hooks/useMobileMenu';
import StoreBanner from '../components/store-banner';
import ServiceFeeInfo from '../components/ServiceFeeInfo';
import danceLocaleIcon from '../assets/dance_locale_3-200h.png';
import './BookingConfirmation.css';
import './booking/BookingSuccessAccountCard.css';
import './header.css';
import './Common.css';
import './Dashboard.css';

/**
 * Booking Success Page
 *
 * Two modes driven by paymentConfirmed in router state:
 *   paymentConfirmed = true  → Stripe payment complete, booking confirmed
 *   paymentConfirmed = false → Auth user, after-lesson, booking request sent
 */
const BookingSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  useMobileMenu();

  // Post-booking "create a free account" card, shown to guests. Dismissible, so it
  // needs state — and this must stay above the no-booking-data early return below,
  // or the hook order would differ between the two render paths.
  const [showCreateAccountCard, setShowCreateAccountCard] = useState(true);

  const bookingData = location.state;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!bookingData) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>No booking data found</h2>
        <button onClick={() => navigate('/')}>Go Home</button>
      </div>
    );
  }

  const {
    instructorName,
    dateDisplay,
    timeSlot,
    totalPrice,
    currency,
    guestEmail = '',
    paymentConfirmed = false,
    bookingStatus = null,
    effectivePricing = null,
  } = bookingData;

  // Trust ladder (PRP booking-confirmation-trust-signal-rethink): after-lesson bookings
  // can come back already confirmed (existing student auto-approved) or pending (newcomer
  // held). Drive the messaging from the response status, never predict from settings.
  // paymentConfirmed = prepaid via Stripe. isAfterLessonConfirmed = confirmed by trust,
  // pay later. Otherwise the booking is pending the instructor's approval.
  const isAfterLessonConfirmed = !paymentConfirmed && bookingStatus === 'confirmed';
  const isConfirmed = paymentConfirmed || isAfterLessonConfirmed;


  return (
    <div className="dashboard-container org-console">
      {/* Standard Navigation Header */}
      <div data-role="Header" className="home-header-container">
        <header className="home-header">
          <a href="/" className="home-logo1">
            <img alt="logo" src={danceLocaleIcon} className="home-image1" />
            <span className="nav-wordmark">Dance Localé</span>
          </a>
          <div className="home-menu1">
            <a href="/" className="home-text10" style={{ textDecoration: 'none' }}>Home</a>
            {user
              ? <>
                  {user.organisationId && <a href="/dashboard" className="home-text12" style={{ textDecoration: 'none' }}>Dashboard</a>}
                  <a href="/my-hub" className="home-text12" style={{ textDecoration: 'none' }}>My Hub</a>
                  <button className="home-text12" onClick={handleLogout} style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'inherit' }}>Logout</button>
                </>
              : <a href="/login" className="home-text12" style={{ textDecoration: 'none' }}>Login</a>
            }
            <a href="/contact" className="home-text13" style={{ textDecoration: 'none' }}>Contact</a>
          </div>
          <div className="home-container11">
            <StoreBanner />
          </div>
        </header>
      </div>

      <div className="dashboard-main">
        <div className="dashboard-content-wrapper" style={{ maxWidth: '560px' }}>

          {/* Single consolidated card */}
          <div className="content-card" style={{ padding: '20px 24px' }}>

            {/* Success header */}
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <Icon
                path={mdiCheckCircle}
                size={2}
                color="var(--dl-color-green-default)"
                style={{ marginBottom: '6px' }}
              />
              <h1 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#333', margin: '0 0 4px' }}>
                {isConfirmed ? 'Booking Confirmed!' : 'Booking Request Sent!'}
              </h1>
              <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: 0 }}>
                {paymentConfirmed
                  ? `Payment received — your lesson with ${instructorName} is confirmed`
                  : isAfterLessonConfirmed
                    ? `Your lesson with ${instructorName} is confirmed`
                    : `Your request has been sent to ${instructorName}`}
              </p>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #f0eaf8', margin: '12px 0' }} />

            {/* Booking details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Icon path={mdiCalendar} size={0.85} color="var(--dl-color-purple-default)" />
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#999' }}>Date</div>
                  <div style={{ fontSize: '0.9rem', color: '#333', fontWeight: '500' }}>{dateDisplay}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Icon path={mdiClock} size={0.85} color="var(--dl-color-purple-default)" />
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#999' }}>Time</div>
                  <div style={{ fontSize: '0.9rem', color: '#333', fontWeight: '500' }}>
                    {timeSlot.startTime} – {timeSlot.endTime}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <Icon path={mdiCurrencyUsd} size={0.85} color="var(--dl-color-purple-default)" style={{ marginTop: '2px' }} />
                <div style={{ flex: 1 }}>
                  {paymentConfirmed ? (
                    <>
                      <div style={{ fontSize: '0.75rem', color: 'var(--dl-color-gray-600)' }}>Amount Paid</div>
                      <div style={{ fontSize: '0.9rem', color: 'var(--dl-color-gray-800)', fontWeight: '500' }}>
                        {formatCurrencyFromMinor(totalPrice, currency)}
                      </div>
                    </>
                  ) : effectivePricing ? (
                    <>
                      {/* Server-computed (effectivePricing, cents). The fee is not an estimate:
                          the only open question for a pay-after-lesson booking is timing. */}
                      <div style={{ fontSize: '0.75rem', color: 'var(--dl-color-gray-600)', marginBottom: '4px' }}>Price</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--dl-color-gray-600)' }}>
                        <span>Lesson fee</span>
                        <span>{formatCurrencyFromMinor(effectivePricing.lessonFee, effectivePricing.currency)}</span>
                      </div>
                      {effectivePricing.venueFee > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--dl-color-gray-600)' }}>
                          <span>Venue hire</span>
                          <span>{formatCurrencyFromMinor(effectivePricing.venueFee, effectivePricing.currency)}</span>
                        </div>
                      )}
                      {effectivePricing.serviceFee > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem', color: 'var(--dl-color-gray-600)' }}>
                          <ServiceFeeInfo />
                          <span>{formatCurrencyFromMinor(effectivePricing.serviceFee, effectivePricing.currency)}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', fontWeight: '600', color: 'var(--dl-color-gray-800)', borderTop: '1px solid var(--dl-color-purple-100)', marginTop: '6px', paddingTop: '6px' }}>
                        <span>Total</span>
                        <span>{formatCurrencyFromMinor(effectivePricing.total, effectivePricing.currency)}</span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--dl-color-gray-600)', marginTop: '4px' }}>
                        Payable after your lesson.
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Payment confirmed badge */}
            {paymentConfirmed && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px' }}>
                <Icon path={mdiCreditCardCheck} size={0.85} color="#16a34a" />
                <div>
                  <div style={{ fontWeight: '600', color: '#15803d', fontSize: '0.85rem' }}>Payment Received</div>
                  <div style={{ fontSize: '0.78rem', color: '#166534' }}>Processed securely via Stripe. A receipt will be sent to your email.</div>
                </div>
              </div>
            )}

            <hr style={{ border: 'none', borderTop: '1px solid #f0eaf8', margin: '12px 0' }} />

            {/* What happens next */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                <Icon path={mdiInformationOutline} size={0.8} color="var(--dl-color-purple-default)" />
                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--dl-color-purple-default)' }}>What Happens Next?</span>
              </div>
              {paymentConfirmed ? (
                <ol style={{ paddingLeft: '18px', color: '#555', lineHeight: '1.65', fontSize: '0.85rem', margin: 0 }}>
                  <li>You'll receive a booking confirmation email shortly</li>
                  <li>The instructor has your contact details if they need to reach you</li>
                  <li>Meet at the scheduled time and enjoy your lesson!</li>
                </ol>
              ) : isAfterLessonConfirmed ? (
                <ol style={{ paddingLeft: '18px', color: '#555', lineHeight: '1.65', fontSize: '0.85rem', margin: 0 }}>
                  <li>You'll receive a booking confirmation email shortly</li>
                  <li>Pay now using the link in your email, or after the lesson</li>
                  <li>Meet at the scheduled time and enjoy your lesson!</li>
                </ol>
              ) : (
                <ol style={{ paddingLeft: '18px', color: '#555', lineHeight: '1.65', fontSize: '0.85rem', margin: 0 }}>
                  <li>{instructorName} will review your booking request</li>
                  <li>You'll receive an email once they approve or respond</li>
                  <li>After approval, use the emailed payment link to pay now or after the lesson</li>
                  <li>Meet at the scheduled time and enjoy your lesson!</li>
                </ol>
              )}
            </div>

            {/* Confirmation note */}
            <div style={{ background: '#f0f9ff', borderRadius: '6px', padding: '8px 12px', fontSize: '0.78rem', color: '#075985', textAlign: 'center', marginBottom: '12px' }}>
              A confirmation email has been sent with your booking details.
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '12px' }}>
              <button
                onClick={() => navigate('/')}
                style={{ padding: '9px 18px', background: 'var(--dl-color-purple-default)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}
              >
                Back to Home
              </button>
              {user && (
                <button
                  onClick={() => navigate('/my-hub')}
                  style={{ padding: '9px 18px', background: 'white', color: 'var(--dl-color-purple-default)', border: '2px solid var(--dl-color-purple-default)', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}
                >
                  View My Bookings
                </button>
              )}
            </div>

            {/* Create-account card — guests only. Mirrors the post-purchase card on
                TicketPurchase.js so the two checkout endings offer the same next step.
                Shown on BOTH endings (paid and pending-approval): either way the guest
                now has a booking to keep track of, so the copy stays outcome-neutral and
                never promises the booking is confirmed. Email is prefilled from the
                address they already gave at checkout. */}
            {!user && showCreateAccountCard && (
              <>
                <hr style={{ border: 'none', borderTop: '1px solid #f0eaf8', margin: '0 0 12px' }} />
                <div className="create-account-card">
                  <h3 className="create-account-card-title">
                    Want to manage your booking?
                  </h3>
                  <ul className="create-account-card-list">
                    <li><Icon path={mdiCheck} size={0.7} color="var(--dl-color-green-default)" style={{ marginRight: '6px', verticalAlign: 'middle' }} />See your lessons in one place</li>
                    <li><Icon path={mdiCheck} size={0.7} color="var(--dl-color-green-default)" style={{ marginRight: '6px', verticalAlign: 'middle' }} />Message your instructor directly</li>
                    <li><Icon path={mdiCheck} size={0.7} color="var(--dl-color-green-default)" style={{ marginRight: '6px', verticalAlign: 'middle' }} />Get lesson reminders</li>
                  </ul>
                  <a
                    href={`/register${guestEmail ? `?email=${encodeURIComponent(guestEmail)}` : ''}`}
                    className="create-account-card-btn"
                  >
                    Create Account
                  </a>
                  <button
                    onClick={() => setShowCreateAccountCard(false)}
                    className="create-account-card-dismiss"
                  >
                    Maybe later
                  </button>
                </div>
              </>
            )}

            <hr style={{ border: 'none', borderTop: '1px solid #f0eaf8', margin: '12px 0' }} />

            {/* App download CTA */}
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: 'var(--dl-color-purple-default)', fontWeight: '600', fontSize: '0.85rem', marginBottom: '2px' }}>
                Want a better experience?
              </p>
              <p style={{ color: '#6b7280', fontSize: '0.8rem', marginBottom: '10px' }}>
                Download the Dance Localé app to manage bookings and discover events.
              </p>
              <StoreBanner />
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccess;
